import React, { useEffect } from 'react'
import { useSharedSettings } from '@/services'
import { modal, ModalParams } from '@codeleap/modals'
import { ActionIcon, Button, Text, View } from '@/components'
import { I18N, useAppI18N, variantProvider } from '@/app'
import { communityAnalytics, onOpenCommunityGuidelines, useIsMobile, verifyFileSize } from '@/utils'
import { useFileInput, FileInput } from '@codeleap/web'
import { navigate } from 'gatsby'
import { WebInputFile } from '@codeleap/common'
import { contributeStore } from '@/stores'
import { Chapter, CommunityFeed } from '@/types'
import { ContributeSizeAlert } from './SizeAlert'
import { CropPickerModal } from '../CropPickerModal'

type ContentTypes = 'photo' | 'text' | 'video'

type ContributeOptionsProps = Partial<ModalParams> & {
  header?: React.ReactNode
  title?: string
  prompt?: CommunityFeed['prompt']
  openCropPicker?: boolean
  lesson?: Chapter['id']
}

const ContributeOptions = (props: ContributeOptionsProps) => {
  const { settings } = useSharedSettings()
  const {
    toggle,
    header,
    title = settings?.contribution?.title ?? 'Contribute with',
    prompt = null,
    openCropPicker = false,
    lesson,
  } = props

  const { t } = useAppI18N()
  const isMobile = useIsMobile()

  const photoInput = useFileInput()
  const videoInput = useFileInput()

  const handlePick = async (contentType: ContentTypes) => {
    communityAnalytics.contributePostOptionEvent(contentType, { ...prompt, source: prompt ? 'prompt' : 'feed' })

    if (contentType === 'text') {
      toggle()
      contributeStore.state.set({ type: contentType, file: null, prompt, lesson })
      navigate('/contribute/text/' as any)
      return
    }

    let files: WebInputFile[] = null

    if (contentType === 'photo') {
      files = await photoInput.openFilePicker()
    } else {
      files = await videoInput.openFilePicker()
    }

    const file = files?.length >= 1 ? files[0] : null

    return processFile(contentType, file)
  }

  const processFile = async (contentType: ContentTypes, providedFile: WebInputFile) => {
    let file = providedFile

    if (file == null) return

    if (contentType === 'video') {
      const validSize = verifyFileSize(file, 500)

      if (!validSize) {
        return ContributeSizeAlert.open()
      }
    } else {
      const cropResult = await CropPickerModal.request({ file })

      if (!cropResult?.file) return

      file = cropResult?.file
    }

    contributeStore.state.set({ type: contentType, file, prompt, lesson })

    toggle()

    navigate('/contribute/file/' as any)
  }

  useEffect(() => {
    const { type, file } = contributeStore.state.get()

    if (openCropPicker && type === 'photo') {
      processFile(type, file)
    }
  }, [])

  const handleClose = () => {
    PostOptionsModal.close()
    return
  }

  return (
    <View
      variants={['column', 'gap:2']}
      responsiveVariants={{
        small: ['column', 'gap:2', 'fullWidth', 'flex', 'alignCenter', 'justifyStart'],
      }}
    >
      {isMobile && (
        <>
          <ActionIcon
            name={'chevron-left'}
            onPress={handleClose}
            debugName={'back prompt button'}
            style={styles.back}
            variants={['minimal', 'iconSize:4']}
            responsiveVariants={{ small: ['flex'] }}
          />
          <Text text={t('modals.prompt.contribute')} variants={['h4', 'bold']} />
        </>
      )}
      {!prompt && <Text text={t('modals.prompt.defaultPrompt')} variants={['h4', 'bold']} />}

      <View variants={['column', 'gap:2', 'fullWidth']}>
        {!prompt ? null : (
          <View variants={['padding:2.5', 'backgroundColor:primary1', 'border-radius:small', 'gap:1', 'column']}>
            {prompt.title ? <Text variants={['h4', 'color:neutral6']} text={prompt.title} /> : null}
            {prompt.description ? <Text variants={['p1', 'color:neutral6']} text={prompt.description} /> : null}
          </View>
        )}
        <Button
          debugName='Contribute with Text'
          text='Text'
          icon='text-cursor'
          variants={['outline:secondary', 'center', 'textCenter']}
          onPress={() => handlePick('text')}
        />
        <Button
          debugName='Contribute with Photo'
          text='Photo'
          icon='image'
          variants={['outline:secondary', 'center', 'textCenter', 'fullWidth']}
          onPress={() => handlePick('photo')}
        />
        <Button
          debugName='Contribute with Video'
          text='Video'
          icon='video'
          variants={['outline:secondary', 'center', 'textCenter']}
          onPress={() => handlePick('video')}
        />
        <Text
          variants={['p4', 'textCenter', 'paddingHorizontal:2', 'color:neutral6']}
          style={isMobile ? { position: 'absolute', bottom: 10, left: 0, width: '100%' } : undefined}
        >
          {'Your contribution will be publicly displayed, and must meet the'}{' '}
          <Text
            component={p => <span {...p} />}
            variants={['p4', 'bold', 'color:primary3', 'cursor:pointer']}
            onPress={onOpenCommunityGuidelines}
            text={I18N.t('communityGuidelines')}
          />
        </Text>
      </View>

      <FileInput multiple={false} ref={videoInput.ref} accept='video/*' />
      <FileInput multiple={false} ref={photoInput.ref} accept='image/*' />
    </View>
  )
}

const MAX_WIDTH = 400

const styles = variantProvider.createComponentStyle(
  theme => ({
    box: {
      maxWidth: MAX_WIDTH,
      width: '100vw',

      // @ts-ignore
      borderRadius: theme.borderRadius.large,
      backgroundColor: theme.colors.neutral1,
      ...theme.spacing.padding(2),

      [theme.media.down('small')]: {
        minHeight: '100dvh',
        maxHeight: '100dvh',
        borderRadius: 0,
      },
    },
    back: {
      position: 'absolute',
      left: theme.spacing.value(2),
      top: theme.spacing.value(2),
      display: 'none',
    },
  }),
  true,
)

export const PostOptionsModal = modal<ContributeOptionsProps>()
  .content(props => <ContributeOptions {...props} />)
  .props({
    variants: ['centered'],
    showClose: false,
    styles: {
      box: styles.box,
      innerWrapper: {
        overflowY: 'hidden',
        maxHeight: '100dvh',
      },
    },
  })
